/* eslint-disable import/max-dependencies */
import { useTranslate } from '@im/react-localization';
import { Breadcrumbs } from '@im/ui-lib/Breadcrumbs';
import { Tag } from '@im/ui-lib/Tag';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { GridColumns } from '@mui/x-data-grid';
import React, { FC, useMemo, useState } from 'react';
import Table, { TableProps } from 'src/components/Table';
import { useDateTimeFormatter } from 'src/hooks/dateTimeFormatter';
import ProgramForm from '../components/ProgramForm';
import DiscountForm from '../components/DiscountForm';
import { useParams } from 'react-router';
import {
  IngramDiscount,
  useGetIngramDiscounts,
  useGetIngramProgramDetails,
} from '../hooks/ingramProgramsSearch';
import Skeleton from '@mui/material/Skeleton';
import { renderCellWithArray } from '../util/renderCellWithArray';
import { StyledText, StyledTextSub } from 'src/components/StyledTypography';
import {
  IconButton,
  InputAdornment,
  SearchIcon,
  TextField,
} from '../components/SearchBarWithChips';
import useDebouncedState from 'src/hooks/useDebouncedState';
import ActionPoper from '../components/ActionPoper';
import ExportComponent from '../components/ExportComponent';

const IngramProgramDetails: FC = () => {
  const translate = useTranslate();
  const formatDateTime = useDateTimeFormatter();
  const { id } = useParams<keyof { id: string }>();

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [discountName, setDiscountName] = useState<string>('');

  const { data: { ingramVendorProgramDetail } = {}, isLoading } =
    useGetIngramProgramDetails(Number(id));

  const getDiscountsRequestBody = {
    ...(Boolean(discountName) && { discountName }),
    ingramProgramId: Number(id),
    page,
    pageSize,
  };

  const { data: { data, responsePreamble } = {}, isLoading: discountLoading } =
    useGetIngramDiscounts(useDebouncedState(getDiscountsRequestBody, 300));

  const columns = useMemo<GridColumns>(
    (): GridColumns<IngramDiscount> => [
      {
        field: 'name',
        flex: 1.5,
        headerName: translate('IngramProgram.DiscountName'),
        minWidth: 140,
        sortable: false,
        valueGetter: ({ row }) => row.name,
      },
      {
        field: 'statusCode',
        flex: 0.75,
        headerName: translate('IngramProgram.Status'),
        minWidth: 140,
        renderCell: ({ row }) => (
          <Tag
            label={translate(
              row.statusCode === 'A'
                ? 'IngramProgram.ActiveNoCount'
                : 'IngramProgram.Deactive'
            )}
            variant={row.statusCode === 'A' ? 'success' : 'default'}
          />
        ),
        sortable: false,
      },
      {
        field: 'effectiveDate',
        flex: 1,
        headerName: translate('IngramProgram.StartDate'),
        minWidth: 140,
        sortable: false,
        valueGetter: ({ row }) => formatDateTime(row.effectiveDate),
      },
      {
        field: 'expirationDate',
        flex: 1,
        headerName: translate('IngramProgram.EndDate'),
        minWidth: 140,
        sortable: false,
        valueGetter: ({ row }) => formatDateTime(row.expirationDate),
      },
      {
        field: 'discountAmount',
        flex: 0.75,
        headerName: translate('IngramProgram.Discount'),
        minWidth: 140,
        renderCell: ({ row }) => {
          return <>{row.discountAmount}</>;
        },
        sortable: false,
      },
      {
        field: 'applyTo',
        flex: 1.5,
        headerName: translate('IngramProgram.ApplyTo'),
        minWidth: 140,
        renderCell: ({ row }) => {
          return (
            <Stack direction={'row'} flexWrap={'wrap'} gap={1}>
              {row.applicability
                .filter((type) => type.attributevalue === 'True')
                .map((type) => (
                  <Tag
                    key={type.attributename}
                    label={type.attributename.split('_')[1]}
                  />
                ))}
            </Stack>
          );
        },
        sortable: false,
      },
      {
        align: 'right',
        field: '',
        renderCell: ({ row }) => <ActionPoper row={row} />,
        sortable: false,
      },
    ],
    [formatDateTime, translate]
  );

  const tableProps: TableProps<IngramDiscount> = {
    columns,
    getRowHeight: () => 'auto',
    loading: discountLoading,
    pagination: { page, pageSize, setPage, setPageSize },
    rows: data ?? [],
    totalCount: responsePreamble?.totalCount,
  };

  const skeleton = (
    <Stack spacing={2}>
      <Skeleton data-testid="Skeleton" height={60} variant="rectangular" />
      <Skeleton height={300} variant="rectangular" />
    </Stack>
  );
  return (
    <Box p={2}>
      {isLoading
        ? skeleton
        : ingramVendorProgramDetail && (
            <Stack gap={2} mb={4}>
              <Breadcrumbs
                currentRoute="ingram-program-detail"
                currentRouteLabel={
                  ingramVendorProgramDetail.requestPreamble.ingramProgramName
                }
                routes={[
                  {
                    key: 'ingramPrograms',
                    label: translate('IngramProgram.Title'),
                    route: '/ingram-program',
                  },
                ]}
              />
              <Stack divider={<Divider />} gap={2}>
                <Stack>
                  <Stack direction={'row'} justifyContent={'space-between'}>
                    <Stack alignItems={'center'} direction={'row'} gap={1}>
                      <Typography
                        sx={{
                          fontSize: '30px',
                          fontWeight: 600,
                          lineHeight: '38px',
                        }}
                      >
                        {
                          ingramVendorProgramDetail.requestPreamble
                            .ingramProgramName
                        }
                      </Typography>
                      <Tag
                        label={`${translate(
                          'IngramProgram.LastModified'
                        )}: ${formatDateTime(
                          ingramVendorProgramDetail.requestPreamble
                            .lastChangedDate
                        )}`}
                      />
                    </Stack>
                    <Stack direction={'row'} gap={1}>
                      <ProgramForm
                        buttonName={translate('Common.Edit')}
                        programDetails={{ ingramVendorProgramDetail }}
                      />
                      <DiscountForm
                        buttonName={translate('IngramProgram.AddNewDiscount')}
                        ingramProgramId={Number(id)}
                      />
                    </Stack>
                  </Stack>
                  <Typography>
                    {
                      ingramVendorProgramDetail.requestPreamble
                        .programsDescription
                    }
                  </Typography>
                </Stack>
                <Stack
                  alignItems="center"
                  direction="row"
                  divider={<Divider flexItem orientation="vertical" />}
                  spacing={2}
                >
                  <Stack>
                    <StyledText fontWeight={600}>
                      {translate('IngramProgram.ProgramType')}
                    </StyledText>
                    <StyledTextSub>
                      {
                        ingramVendorProgramDetail.manualTemplatesFieldProperties
                          .vendorACOPDiscountType
                      }
                    </StyledTextSub>
                  </Stack>
                  <Stack>
                    <StyledText fontWeight={600}>
                      {translate('IngramProgram.ProductGroup')}
                    </StyledText>
                    <StyledTextSub>
                      {renderCellWithArray(
                        ingramVendorProgramDetail.requestPreamble.productGroupInfo.map(
                          (productGroup) => productGroup.productGroupName
                        )
                      )}
                    </StyledTextSub>
                  </Stack>
                  <Stack>
                    <StyledText fontWeight={600}>
                      {translate('IngramProgram.CustomerGroup')}
                    </StyledText>
                    <StyledTextSub>
                      {renderCellWithArray(
                        ingramVendorProgramDetail.requestPreamble.customerGroupInfo.map(
                          (customerGroup) => customerGroup.customergroupname
                        )
                      )}
                    </StyledTextSub>
                  </Stack>
                </Stack>
              </Stack>
              <Stack direction={'row'} justifyContent={'space-between'}>
                <TextField
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton>
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  name="ingramDiscountSearchField"
                  onChange={(event) => setDiscountName(event.target.value)}
                  placeholder={translate(
                    'IngramProgram.DiscountNamePlaceholder'
                  )}
                  sx={{ width: '30%' }}
                  value={discountName}
                  variant="outlined"
                />
                <ExportComponent
                  isProgramOrDiscount={false}
                  params={getDiscountsRequestBody}
                />
              </Stack>
              <Table<IngramDiscount> {...tableProps} />
            </Stack>
          )}
    </Box>
  );
};

export default IngramProgramDetails;
